/**
 * Animated second title line for the page hero (see page_hero_block() in
 * page-hero.php). Items flip up like a rolling clock drum: the current item
 * tips back over the top as the next rolls in from below.
 */
class VTSPageHeroLines {
    static FALLBACK_DELAY = 5;
    static DURATION = 0.6;

    constructor(element) {
        this.element = element;
        this.items = [...element.querySelectorAll('.page-hero__animated-item')];

        if (this.items.length < 2) {
            return;
        }

        if (typeof gsap === 'undefined') {
            Object.assign(this.items[0].style, {
                opacity: 1,
                visibility: 'visible'
            });
            return;
        }

        this.times = this.items.map(
            (item) => parseFloat(item.dataset.time) || 0
        );

        this.activeIndex = -1;
        this.mode = null;
        this.ticking = false;

        const prefersReducedMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches;
        // Reduced motion gets a cross-fade with no flip.
        this.angle = prefersReducedMotion ? 0 : 90;

        const player =
            element.dataset.sync === 'video' && !prefersReducedMotion
                ? element.closest('.page-hero')?.querySelector('wistia-player')
                : null;

        if (player) {
            this.syncToVideo(player);
        } else {
            this.runDelay();
        }
    }

    syncToVideo(player) {
        this.mode = 'video';

        // Covers the video failing to load, player.js being blocked, and
        // autoplay being refused.
        const fallback = gsap.delayedCall(
            VTSPageHeroLines.FALLBACK_DELAY,
            () => {
                this.stopTicking();
                this.runDelay();
            }
        );

        let started = false;

        // Read on GSAP's ticker rather than Wistia's time events, which only
        // fire a few times a second.
        this.tick = () => {
            const time = player.currentTime || 0;

            if (!started && time > 0) {
                started = true;
                fallback.kill();
            }

            if (started) {
                this.show(this.indexAt(time));
            }
        };

        const start = () => {
            if (this.mode === 'video' && !this.ticking) {
                this.ticking = true;
                gsap.ticker.add(this.tick);
            }
        };

        customElements.whenDefined('wistia-player').then(start);
        player.addEventListener('play', start);
        player.addEventListener('pause', () => this.stopTicking());
        player.addEventListener('ended', () => this.stopTicking());
    }

    stopTicking() {
        if (this.ticking) {
            this.ticking = false;
            gsap.ticker.remove(this.tick);
        }
    }

    runDelay() {
        this.mode = 'delay';

        this.times.forEach((time, i) => {
            gsap.delayedCall(time, () => this.show(i));
        });
    }

    indexAt(time) {
        let index = 0;

        for (let i = 1; i < this.times.length; i++) {
            if (this.times[i] <= time) {
                index = i;
            }
        }

        return index;
    }

    show(index) {
        if (index === this.activeIndex) {
            return;
        }

        const previous = this.items[this.activeIndex];
        const next = this.items[index];

        // Rotating around an axis half a line behind the text turns the items
        // like faces of a drum rather than flat cards spinning in place.
        const depth = next.offsetHeight / 2;

        const tween = {
            duration: VTSPageHeroLines.DURATION,
            ease: 'power2.inOut',
            transformOrigin: `50% 50% -${depth}px`,
            // Interrupts any tween still running on the item, e.g. when a
            // looping video jumps back to an item that has only just left.
            overwrite: true
        };

        if (previous) {
            gsap.to(previous, {
                ...tween,
                rotationX: this.angle,
                autoAlpha: 0
            });
        }

        gsap.fromTo(
            next,
            { rotationX: -this.angle, autoAlpha: 0 },
            { ...tween, rotationX: 0, autoAlpha: 1 }
        );

        this.activeIndex = index;
    }
}

document
    .querySelectorAll('[data-page-hero-lines]')
    .forEach((element) => new VTSPageHeroLines(element));
